// Interactive preview helpers: one per-layer transform shared by drawing,
// hit-testing and camera framing, plus the easing used by the animations.

export const PART_STEPS = ['framesets', 'bars', 'groupsets', 'wheels', 'tyres', 'seatposts', 'saddles', 'cages', 'pedals'];

export const PART_LABELS = {
  framesets: 'Frameset', bars: 'Cockpit', groupsets: 'Groupset', wheels: 'Wheels',
  tyres: 'Tyres', seatposts: 'Seatpost', saddles: 'Saddle', cages: 'Bottle cages', pedals: 'Pedals'
};

export function partStep(type = '') {
  return PART_STEPS.find(step => type.startsWith(step)) || null;
}

export const ease = {
  out3: p => 1 - Math.pow(1 - p, 3),
  inOut3: p => p < .5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2,
  inOutSine: p => -(Math.cos(Math.PI * p) - 1) / 2,
  // Slight overshoot that settles by p = 1.
  spring: p => p >= 1 ? 1 : 1 - Math.exp(-6 * p) * Math.cos(9 * p)
};

export const clamp = (v, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, v));

const deg = rad => rad * 180 / Math.PI;

// Wheels, tyres and rotors turn about the axle. Cranks turn when their art is
// centred on the spindle: calibrated catalogue cutouts, and the CCACHE rings
// and arm layers (`turns`), whose derailleur and chain live in a static layer.
export function wheelPart(item) {
  return /^(wheels|tyres)(Left|Right)$|^groupsetsBrake(Left|Right)$/.test(item.type);
}

export function crankPart(item) {
  if (item.drivetrain) return !!item.turns;
  return item.type === 'groupsetsMiddle' && !!item.calibration;
}

// Current turn of a crank layer: its own spin-in plus the ride animation.
export function crankTurn(item, motion = {}) {
  if (!crankPart(item)) return 0;
  const own = motion[item.type];
  return (own && own.spin || 0) + (motion.ride ? motion.ride.crank : 0);
}

// Mirrors the transform sequence Bike.vue has always drawn with; `motion`
// adds the animated offset, scale and rotation about the layer origin.
export function layerMatrix(item, motion = {}) {
  const m = new DOMMatrix();
  const own = motion[item.type] || (item.parent && motion[item.parent.type]) || null;
  if (own && (own.dx || own.dy)) m.translateSelf(own.dx || 0, own.dy || 0);

  if (!item.parent) {
    m.translateSelf(item.x, item.y);
    if (item.type === 'framesets') return m;
    if (item.rotation && !item.blockRotation) m.rotateSelf(deg(-item.rotation));
    if (item.flipX) m.scaleSelf(-1, 1);
    let turn = own && own.spin || 0;
    if (motion.ride && wheelPart(item)) turn += motion.ride.wheel;
    if (motion.ride && crankPart(item)) turn += motion.ride.crank;
    // Turn about the spindle when it differs from the mounting origin.
    const pivot = item.pivot ? { x: (item.pivot.x - item.origin.x) * item.scale, y: (item.pivot.y - item.origin.y) * item.scale } : null;
    if (turn && pivot) m.translateSelf(pivot.x, pivot.y).rotateSelf(deg(turn)).translateSelf(-pivot.x, -pivot.y);
    else if (turn) m.rotateSelf(deg(turn));
    if (own && own.scale != null && own.scale !== 1) m.scaleSelf(own.scale, own.scale);
    m.translateSelf(-item.origin.x * item.scale, -item.origin.y * item.scale);
    return m;
  }

  const parent = item.parent;
  m.translateSelf(parent.x, parent.y);
  if (parent.rotation) m.rotateSelf(deg(-parent.rotation));
  let ax = (item.anchor.x - parent.origin.x) * parent.scale;
  let ay = (item.anchor.y - parent.origin.y) * parent.scale;
  // A pedal rides round with the crank's pedal eye but is never rotated itself.
  const turn = item.followsCrank ? crankTurn(parent, motion) : 0;
  if (turn) {
    const px = parent.pivot ? (parent.pivot.x - parent.origin.x) * parent.scale : 0;
    const py = parent.pivot ? (parent.pivot.y - parent.origin.y) * parent.scale : 0;
    const cos = Math.cos(turn), sin = Math.sin(turn), dx = ax - px, dy = ay - py;
    ax = px + dx * cos - dy * sin;
    ay = py + dx * sin + dy * cos;
  }
  m.translateSelf(ax, ay);
  if (item.blockRotation) {
    if (parent.rotation) m.rotateSelf(deg(parent.rotation));
  } else if (item.rotation) {
    m.rotateSelf(deg(-item.rotation));
  }
  m.translateSelf(-item.origin.x * item.scale, -item.origin.y * item.scale);
  return m;
}

// Drawn area of a layer in its own (pre-matrix) coordinates.
export function layerBox(item) {
  const left = item.cropLeft != null ? item.cropLeft * item.scale : 0;
  const right = item.cropRight != null ? item.cropRight * item.scale : item.width;
  const extra = item.rearImageSplit ? Math.max(0, item.rearImageSplit.offsetY * item.scale) : 0;
  return { left, top: 0, right, bottom: item.height + extra };
}

export function boundsOf(matrix, box) {
  const pts = [[box.left, box.top], [box.right, box.top], [box.left, box.bottom], [box.right, box.bottom]]
    .map(([x, y]) => matrix.transformPoint(new DOMPoint(x, y)));
  const xs = pts.map(p => p.x), ys = pts.map(p => p.y);
  return { left: Math.min(...xs), top: Math.min(...ys), right: Math.max(...xs), bottom: Math.max(...ys) };
}

let probe = null;
function probeContext() {
  if (!probe) {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 1;
    probe = canvas.getContext('2d', { willReadFrequently: true });
  }
  return probe;
}

// Is the layer's artwork opaque under this local point? Reads one source pixel.
export function opaqueAt(item, x, y) {
  const box = layerBox(item);
  if (x < box.left || x > box.right || y < box.top || y > box.bottom) return false;
  const image = item.image;
  if (!image || !image.naturalWidth) return false;
  const kx = image.naturalWidth / item.width, ky = image.naturalHeight / item.height;
  let sy = y * ky;
  const split = item.rearImageSplit;
  if (split) {
    const lower = (split.y + split.offsetY) * item.scale;
    if (y >= lower) sy = y / item.scale - split.offsetY;
    else if (y > split.y * item.scale) return false;
    else sy = y / item.scale;
  }
  const sx = x * kx;
  if (sx < 0 || sy < 0 || sx >= image.naturalWidth || sy >= image.naturalHeight) return false;
  try {
    const ctx = probeContext();
    ctx.clearRect(0, 0, 1, 1);
    ctx.drawImage(image, Math.floor(sx), Math.floor(sy), 1, 1, 0, 0, 1, 1);
    return ctx.getImageData(0, 0, 1, 1).data[3] > 24;
  } catch (e) {
    // Cross-origin artwork cannot be read back; fall back to its rectangle.
    return true;
  }
}

// Grow a rectangle by `pad` and to the canvas aspect ratio, never zooming in
// further than `maxZoom` so the raster artwork stays sharp.
export function frameRect(rect, width, height, pad = .15, maxZoom = 3) {
  let w = (rect.right - rect.left) * (1 + pad * 2);
  let h = (rect.bottom - rect.top) * (1 + pad * 2);
  const aspect = width / height;
  if (w / h > aspect) h = w / aspect; else w = h * aspect;
  if (w < width / maxZoom) { w = width / maxZoom; h = w / aspect; }
  const cx = (rect.left + rect.right) / 2, cy = (rect.top + rect.bottom) / 2;
  return { x: cx - w / 2, y: cy - h / 2, w, h };
}

export function lerpRect(a, b, p) {
  return { x: a.x + (b.x - a.x) * p, y: a.y + (b.y - a.y) * p, w: a.w + (b.w - a.w) * p, h: a.h + (b.h - a.h) * p };
}
